// ログイン画面
import { useState } from 'react';
import { useTheme } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import Container from '@mui/material/Container';
import FormControl from '@mui/material/FormControl';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';

import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import loginLogoIcon from '../images/login_logo.svg';

const Login = () => {
  const navigate = useNavigate();
  const [loginErrorMessage, setLoginErrorMessage] = useState('');

  const { typography } = useTheme();
  const styles = {
    formItemTitle: {
      ...typography.caption13bold,
      marginBottom: '4px',
    },
    loginButton: {
      ...typography.caption13bold,
      width: '280px',
      height: '44px',
      backgroundColor: 'primary.light',
      borderRadius: '100px',
    },
    ErrorMessage: {
      ...typography.caption12,
      margin: '4px 14px 0',
      color: 'error.main',
    },
  };

  const {
    handleSubmit,
    control,
  } = useForm();

  // ログインボタン押下後のログイン可否判断
  const onSubmit = async (loginInfo) => {
    try {
      const body = {
        organization: loginInfo.organization,
        employeeNumber: loginInfo.employeeNumber,
        password: loginInfo.password,
      };
      const res = await axios.post('/v1/auth/web/login', body);
      navigate('/', { state: { loginUser: res.data.user }, replace: true });
    } catch (error) {
      setLoginErrorMessage(error.loginFailed);
    }
  };

  // 乗務員区所選択プルダウン
  const OrganizationPullDown = () => {
    return (
      <Stack sx={{ marginBottom: '24px' }}>
        <Typography sx={styles.formItemTitle}>乗務員区所</Typography>
        <Controller
          control={control}
          name='organization'
          defaultValue=''
          rules={{ required: { value: true, message: '乗務員区所を選択してください。' } }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              select
              label='選択する'
              size='small'
              error={!!error?.message}
              helperText={error?.message}
            >
              {/* TODO: v1/organizationsで取得した運輸区のcodeとnameに変更する */}
              <MenuItem value={'01'}>新潟運輸区</MenuItem>
              <MenuItem value={'02'}>仙台統括センター（仙台）</MenuItem>
              <MenuItem value={'03'}>仙台統括センター（宮城野）</MenuItem>
            </TextField>
          )}>
        </Controller>
      </Stack>
    );
  };

  // 社員番号入力フォーム
  const EmployeeNumberFields = () => {
    return (
      <Stack sx={{ marginBottom: '24px' }}>
        <Typography sx={styles.formItemTitle}>アカウント（社員番号 数字７桁）</Typography>
        <Controller
          control={control}
          name='employeeNumber'
          defaultValue=''
          rules={{
            required: { value: true, message: '社員番号を入力してください。' },
            minLength: { value: 7, message: '7文字で入力してください。' },
            maxLength: { value: 7, message: '7文字で入力してください。' },
            pattern: { value: /[0-9]{7}/, message: '数字で入力してください。' },
          }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              size='small'
              error={!!error?.message}
              helperText={error?.message}
            />
          )}>
        </Controller>
      </Stack>
    );
  };

  // パスワード入力フォーム
  const PasswordFields = () => {
    const [showPassword, setShowPassword] = useState('');
    return (
      <Stack>
        <Typography sx={styles.formItemTitle}>パスワード</Typography>
        <Controller
          control={control}
          name='password'
          defaultValue=''
          rules={{
            required: { value: true, message: 'パスワードを入力してください。' },
          }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              size='small'
              autoComplete='on'
              type={showPassword ? 'text' : 'password'}
              InputProps={{
                endAdornment:
                  <InputAdornment position='end'>
                    <IconButton
                      aria-label='toggle password visibility'
                      onClick={() => setShowPassword(!showPassword)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge='end'
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>,
              }}
              error={!!error?.message}
              helperText={error?.message}
            />
          )}>
        </Controller>
      </Stack>
    );
  };

  // ログインボタン・ログイン失敗時のエラーメッセージ
  const LoginButton = () => {
    return (
      <Stack sx={{ marginTop: '48px' }}>
        <Button variant='contained' type='submit' sx={styles.loginButton}>ログイン</Button>
        <Typography sx={styles.ErrorMessage}>{loginErrorMessage}</Typography>
      </Stack>
    );
  };

  return (
    <Container disableGutters sx={{ width: '380px', margin: '50px auto auto' }}>
      <Stack sx={{width: '280px', margin: '0 auto'}}>
        <Stack sx={{ marginBottom: '35px', alignItems: 'center' }}>
          <Typography sx={{ ...typography.caption13bold, marginBottom: '8px' }}>【指導用】</Typography>
          <Avatar src={loginLogoIcon} variant='square' alt='つうトレ' sx={{ width: '160px', height: '47px' }} />
        </Stack>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormControl fullWidth size='small'>
            {/* <OrganizationPullDown /> */}
            <Stack sx={{ height: '40px' }} /> {/* TODO: 区所選択のプルダウンを表示する場合はこのStackを削除すること */}
            <EmployeeNumberFields />
            <PasswordFields />
            <LoginButton />
          </FormControl>
        </form>
      </Stack>
      <Stack sx={{ marginTop: '68px'}}>
        <Typography sx={{ ...typography.caption11half,margin: 'auto'}}>お問い合わせは、各事業本部の乗務員指導または</Typography>
        <Typography sx={{ ...typography.caption11half,margin: 'auto'}}>本社オペレーションマネジメントユニット（乗務員）までお願いします。</Typography>
      </Stack>
    </Container>
  );
};

export default Login;